import React from 'react';
import { Link } from 'react-router-dom';
import { useT, useLang } from '../i18n';

const T = {
  es: {
    tagline: 'Plataforma modular de visibilidad, inteligencia y documentos digitales para la Cadena de Suministro.',
    products: 'Productos',
    company: 'Empresa',
    aboutCadenia: 'Sobre Cadenia',
    contact: 'Contacto',
    requestDemo: 'Solicitar demo',
    contactHeading: 'Contacto',
    region: 'España · Europa',
    infra: 'Infraestructura 100% serverless en AWS Europa. Cumplimiento GDPR. Datos alojados en la UE.',
    rights: 'Todos los derechos reservados.',
    privacyPolicy: 'Política de Privacidad',
  },
  en: {
    tagline: 'Modular visibility, intelligence and digital documents platform for the Supply Chain.',
    products: 'Products',
    company: 'Company',
    aboutCadenia: 'About Cadenia',
    contact: 'Contact',
    requestDemo: 'Request a demo',
    contactHeading: 'Contact',
    region: 'Spain · Europe',
    infra: '100% serverless infrastructure on AWS Europe. GDPR compliant. Data hosted in the EU.',
    rights: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
  },
};

const Footer = () => {
  const t = useT(T);
  const { path } = useLang();
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
          {/* Logo y descripción */}
          <div className="col-span-2 md:col-span-4">
            <div className="mb-6">
              <img
                src="/images/logo-cadenia-300x100.svg"
                alt="Cadenia"
                className="h-10 w-auto filter brightness-0 invert opacity-90"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              {t.tagline}
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.linkedin.com/company/81317793" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all" aria-label="LinkedIn">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="mailto:info@cadenia.eu" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all" aria-label="Email">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Productos */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-xs font-medium mb-5 uppercase tracking-wider text-slate-500">{t.products}</h4>
            <ul className="space-y-3">
              <li><Link to={path('visibility')} className="text-slate-400 hover:text-white transition-colors text-sm">Visibility</Link></li>
              <li><Link to={path('intelligence')} className="text-slate-400 hover:text-white transition-colors text-sm">Intelligence</Link></li>
              <li><Link to={path('documents')} className="text-slate-400 hover:text-white transition-colors text-sm">Documents</Link></li>
            </ul>
          </div>

          {/* Empresa */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-xs font-medium mb-5 uppercase tracking-wider text-slate-500">{t.company}</h4>
            <ul className="space-y-3">
              <li><Link to={path('about')} className="text-slate-400 hover:text-white transition-colors text-sm">{t.aboutCadenia}</Link></li>
              <li><Link to={path('contact')} className="text-slate-400 hover:text-white transition-colors text-sm">{t.contact}</Link></li>
              <li><a href={`${path('home')}#contacto`} className="text-slate-400 hover:text-white transition-colors text-sm">{t.requestDemo}</a></li>
            </ul>
          </div>

          {/* Legal + Contacto */}
          <div className="col-span-2 md:col-span-4">
            <h4 className="text-xs font-medium mb-5 uppercase tracking-wider text-slate-500">{t.contactHeading}</h4>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                <a href="mailto:info@cadenia.eu" className="text-slate-400 hover:text-white transition-colors text-sm">info@cadenia.eu</a>
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-slate-400 text-sm">{t.region}</span>
              </li>
            </ul>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                {t.infra}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-14 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">
              © {new Date().getFullYear()} Cadenia. {t.rights}
            </p>
            <div className="flex items-center gap-6">
              <Link to={path('privacy')} className="text-slate-500 hover:text-slate-300 transition-colors text-xs">
                {t.privacyPolicy}
              </Link>
              <span className="text-slate-700">·</span>
              <a href="mailto:info@cadenia.eu" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">
                info@cadenia.eu
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
